/**
 * AcenteReservationForm — step-wizard form for creating/editing reservations.
 *
 * Redesigned 2026-02 to reduce time-to-completion for high-volume agencies.
 * Structure:
 *   [ AI photo shortcut ]         — always visible; can jump-fill any step
 *   [ Live tiles: gece / oda / kap / kişi ]
 *   [ Stepper 1..4 ]
 *     1. Otel & Grup   (hotel dropdown + group name)
 *     2. Tarihler      (checkin / checkout with quick +N gün)
 *     3. Kişi & Yemek  (adults/children/staff + meal_start/end)
 *     4. Oda & Not     (room breakdown + notes + submit)
 *
 * Agencies can still see the whole picture via live tiles and the inline
 * capacity warning, but only need to focus on ONE section at a time.
 *
 * Keyboard: <Enter> in any input advances to the next step (or submits on
 * the last one). "Geri" and "Devam" buttons drive navigation.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { numberFieldProps, dateFieldProps } from "@/lib/numberInput";
import { NumberInput } from "@/components/NumberInput";
import { DatePicker } from "@/components/DatePicker";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Save, ArrowLeft, ArrowRight, BedDouble, Users, Calendar, Sparkles, Camera,
  Loader2, AlertTriangle, CheckCircle2, Hotel, Utensils, StickyNote, Wand2,
  Wallet,
} from "lucide-react";
import CapacityMismatchModal from "./CapacityMismatchModal";
import HotelCombobox from "./HotelCombobox";
import { computeReservationTotal, totalMealCount } from "@/lib/pricing";

// Local helper — a checkin/checkout pair's night count. Duplicated instead
// of imported so the form works even when either date is empty (returns 0
// rather than throwing).
function _computedNights(ci, co) {
  if (!ci || !co) return 0;
  const a = new Date(ci + "T00:00:00");
  const b = new Date(co + "T00:00:00");
  const ms = b - a;
  return ms > 0 ? Math.round(ms / 86400000) : 0;
}

function emptyForm() {
  return {
    group_name: "",
    agency_id: "",
    hotel_name: "",
    checkin: "",
    checkout: "",
    notes: "",
    rooms: { r1: 0, r2: 0, r3: 0, r4: 0, r5: 0 },
    adults: 1,
    children: 0,
    staff: 0,
    meal_start: "kahvalti",
    meal_end: "aksam",
    agreed_room_price: 0,
    agreed_meal_price: 0,
    agreed_price_currency: "SAR",
    // Iter 26: user enters UNIT prices; agreed_* is derived at save time.
    room_unit_price: 0,   // SAR per oda per gece
    meal_unit_price: 0,   // SAR per kişi per gün (2 öğün) — /2 = per meal
  };
}

// UX-configurable meal options. Öğle removed per business request (2026-02).
// Legacy 'ogle' still parseable in backend for backward-compat with old records
// and AI extraction, but no longer selectable in the UI.
const MEAL_OPTIONS = [
  { v: "kahvalti", t: "Kahvaltı" },
  { v: "aksam", t: "Akşam" },
];

// Shared UX helpers moved to /app/frontend/src/lib/numberInput.js — see
// that file for the rationale. Importing keeps the fix in one place.
// import at top of file.

// Wizard steps — the label + icon is shown in the stepper header.
const STEPS = [
  { key: "otel_tarih",  label: "Otel & Tarih",  icon: Hotel },
  { key: "kisi_oda",    label: "Kişi & Oda",    icon: Users },
  { key: "fiyat",       label: "Fiyat & Not",   icon: Wallet },
];

export default function AcenteReservationForm() {
  const { user } = useAcenteAuth();
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(emptyForm());
  const [agencies, setAgencies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0); // 0..3

  // AI photo extraction state
  const aiFileRef = useRef(null);
  const [aiBusy, setAiBusy] = useState(false);

  // If launched with ?mode=ai (from ReservationMethodChooser), auto-open the
  // native file picker on mount so the agency doesn't have to click twice.
  useEffect(() => {
    if (!isEdit && searchParams.get("mode") === "ai") {
      // Small delay so the ref is attached and the sheet feels intentional.
      const t = setTimeout(() => aiFileRef.current?.click(), 400);
      return () => clearTimeout(t);
    }
  }, [isEdit, searchParams]);

  /** Merge AI-extracted fields into the form, only overwriting empty values so
   * user edits are never trampled. */
  const applyAiData = (d) => {
    setForm((f) => {
      const next = { ...f };
      if (d.group_name && !f.group_name) next.group_name = d.group_name;
      if (d.hotel_name && !f.hotel_name) next.hotel_name = d.hotel_name;
      if (d.checkin && !f.checkin) next.checkin = d.checkin;
      if (d.checkout && !f.checkout) next.checkout = d.checkout;
      if (d.adults != null) next.adults = d.adults;
      if (d.children != null) next.children = d.children;
      if (d.staff != null) next.staff = d.staff;
      if (d.rooms) next.rooms = { r1: d.rooms.r1 || 0, r2: d.rooms.r2 || 0, r3: d.rooms.r3 || 0, r4: d.rooms.r4 || 0, r5: d.rooms.r5 || 0 };
      if (d.meal_start) next.meal_start = d.meal_start;
      if (d.meal_end) next.meal_end = d.meal_end;
      if (d.notes && !f.notes) next.notes = d.notes;
      return next;
    });
  };

  const handleAiUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const ok = ["image/jpeg", "image/png", "image/webp"];
    if (files.some((f) => !ok.includes(f.type))) {
      toast.error("Sadece JPEG/PNG/WEBP dosyaları desteklenir");
      e.target.value = "";
      return;
    }
    if (files.some((f) => f.size > 8 * 1024 * 1024)) {
      toast.error("Her fotoğraf en fazla 8 MB olabilir");
      e.target.value = "";
      return;
    }
    if (files.length > 6) {
      toast.error("En fazla 6 fotoğraf yükleyebilirsiniz");
      e.target.value = "";
      return;
    }

    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));

    setAiBusy(true);
    try {
      const r = await panelApi.post("/ai/extract-reservation", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      const data = r?.data?.data || {};
      applyAiData(data);
      toast.success("Bilgiler formu dolduruldu — kontrol edip kaydedin", { duration: 5000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bilgi çıkarılamadı. Daha net bir fotoğraf deneyin.");
    } finally {
      setAiBusy(false);
      e.target.value = "";
    }
  };

  // Live calculations so the operator sees the impact of their entries.
  const nights = useMemo(() => {
    if (!form.checkin || !form.checkout) return 0;
    const a = new Date(form.checkin);
    const b = new Date(form.checkout);
    return Math.max(0, Math.round((b - a) / 86400000));
  }, [form.checkin, form.checkout]);

  const totalRooms = useMemo(
    () => (form.rooms.r1 || 0) + (form.rooms.r2 || 0) + (form.rooms.r3 || 0) + (form.rooms.r4 || 0) + (form.rooms.r5 || 0),
    [form.rooms]
  );

  const roomCapacity = useMemo(
    () =>
      1 * (form.rooms.r1 || 0) +
      2 * (form.rooms.r2 || 0) +
      3 * (form.rooms.r3 || 0) +
      4 * (form.rooms.r4 || 0) +
      5 * (form.rooms.r5 || 0),
    [form.rooms]
  );

  const totalPeople = useMemo(
    () => (form.adults || 0) + (form.children || 0) + (form.staff || 0),
    [form.adults, form.children, form.staff]
  );

  const capacityDiff = roomCapacity - totalPeople;
  const showCapacityWarn = totalRooms > 0 && totalPeople > 0 && capacityDiff !== 0;

  useEffect(() => {
    if (user && user.role !== "acente") {
      panelApi.get("/agencies").then((r) => setAgencies(r.data)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (!isEdit) return;
    panelApi.get(`/reservations/${id}`).then((r) => {
      const d = r.data;
      setForm({
        group_name: d.group_name || "",
        agency_id: d.agency_id || "",
        hotel_name: d.hotel_name || "",
        checkin: d.checkin || "",
        checkout: d.checkout || "",
        notes: d.notes || "",
        rooms: { r1: d.rooms?.r1 || 0, r2: d.rooms?.r2 || 0, r3: d.rooms?.r3 || 0, r4: d.rooms?.r4 || 0, r5: d.rooms?.r5 || 0 },
        adults: d.adults ?? 1,
        children: d.children ?? 0,
        staff: d.staff ?? 0,
        meal_start: d.meal_start || "kahvalti",
        meal_end: d.meal_end || "aksam",
        agreed_room_price: d.agreed_room_price ?? 0,
        agreed_meal_price: d.agreed_meal_price ?? 0,
        agreed_price_currency: d.agreed_price_currency || "SAR",
        room_unit_price: d.room_unit_price ?? 0,
        meal_unit_price: d.meal_unit_price ?? 0,
      });
    }).catch(() => toast.error("Rezervasyon yüklenemedi"));
  }, [id, isEdit]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRoom = (k, v) => setForm((f) => ({ ...f, rooms: { ...f.rooms, [k]: Math.max(0, parseInt(v || 0, 10)) } }));
  const setNum = (k, v) => setForm((f) => ({ ...f, [k]: Math.max(k === "adults" ? 1 : 0, parseInt(v || 0, 10)) }));

  // Iter 39: Kayıtlı acente-otel fiyatı otomatik doldurma.
  // Yeni rezervasyon oluşturulurken (edit değil) kullanıcı otel adını yazınca
  // veya acente değiştirince backend'den kayıtlı birim fiyatı çeker ve boş
  // olan alanları doldurur. Kullanıcı yazdığı fiyata dokunulmaz.
  const [autofilledPrice, setAutofilledPrice] = useState(null);
  useEffect(() => {
    if (isEdit) return;
    const agencyId = form.agency_id || user?.agency_id;
    const hotelName = (form.hotel_name || "").trim();
    if (!agencyId || !hotelName) {
      setAutofilledPrice(null);
      return;
    }
    let cancelled = false;
    panelApi
      .get(`/agency-price-lookup?agency_id=${encodeURIComponent(agencyId)}&hotel_name=${encodeURIComponent(hotelName)}`)
      .then((r) => {
        if (cancelled) return;
        const p = r.data || {};
        setForm((f) => {
          const next = { ...f };
          // Sadece 0 olan alanları doldur — kullanıcı zaten girmişse dokunma.
          if ((!f.room_unit_price || f.room_unit_price === 0) && (p.room_unit_price || 0) > 0) {
            next.room_unit_price = p.room_unit_price;
          }
          if ((!f.meal_unit_price || f.meal_unit_price === 0) && (p.meal_unit_price || 0) > 0) {
            next.meal_unit_price = p.meal_unit_price;
          }
          return next;
        });
        setAutofilledPrice({ room: p.room_unit_price, meal: p.meal_unit_price });
      })
      .catch(() => setAutofilledPrice(null));
    return () => { cancelled = true; };
  }, [form.agency_id, form.hotel_name, isEdit, user?.agency_id]);

  // Quick date helpers — bumps checkout by N nights from checkin.
  const bumpCheckout = (n) => {
    if (!form.checkin) return toast.error("Önce giriş tarihini seçin");
    const d = new Date(form.checkin);
    d.setDate(d.getDate() + n);
    setField("checkout", d.toISOString().slice(0, 10));
  };

  // Automatic room suggestion — packs the total people count into the
  // fewest rooms starting with the largest room type. Only writes when
  // capacity < people (i.e. still under-provisioned) so we don't overwrite
  // a valid manual entry.
  const suggestRooms = () => {
    let remaining = totalPeople;
    if (remaining <= 0) return;
    const rooms = { r1: 0, r2: 0, r3: 0, r4: 0, r5: 0 };
    // Prefer 2- and 3-bed rooms — closest to typical Umre allocations.
    for (const [k, size] of [["r3", 3], ["r2", 2], ["r4", 4], ["r5", 5], ["r1", 1]]) {
      if (remaining <= 0) break;
      const n = Math.floor(remaining / size);
      if (n > 0) { rooms[k] = n; remaining -= n * size; }
    }
    if (remaining > 0) rooms.r1 += remaining;
    setForm((f) => ({ ...f, rooms }));
    toast.success("Oda dağılımı önerildi — kontrol edin");
  };

  // Capacity confirmation modal state.
  const [capacityModalOpen, setCapacityModalOpen] = useState(false);

  const doPersist = async () => {
    setCapacityModalOpen(false);
    setBusy(true);
    try {
      // Compute the total agreed prices from the unit prices the user
      // entered. This is where the wizard's new pricing model (unit prices
      // in, totals out) is materialised so operasyon sees a single
      // aggregate number consistent with the rest of the app.
      const { roomTotal, mealTotal } = computeReservationTotal({
        rooms: form.rooms,
        nights: _computedNights(form.checkin, form.checkout),
        adults: form.adults,
        children: form.children,
        staff: form.staff,
        meal_start: form.meal_start,
        meal_end: form.meal_end,
        room_unit_price: form.room_unit_price,
        meal_unit_price: form.meal_unit_price,
      });
      const payload = {
        ...form,
        agreed_room_price: roomTotal,
        agreed_meal_price: mealTotal,
      };
      if (user?.role === "acente") delete payload.agency_id;
      if (isEdit) {
        await panelApi.put(`/reservations/${id}`, payload);
        toast.success("Rezervasyon güncellendi");
      } else {
        await panelApi.post("/reservations", payload);
        toast.success("Rezervasyon oluşturuldu");
      }
      nav("/acente/rezervasyonlar");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : typeof detail === "string" ? detail : "Kayıt yapılamadı";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // Per-step validation. Returns null on OK, otherwise a toast-friendly reason.
  const validateStep = (s) => {
    if (s === 0) {
      // Iter 31: birleşik "Otel & Tarih" adımı — hem otel/acente hem
      // tarihleri buradan denetliyoruz.
      if (!form.hotel_name.trim()) return "Otel adı zorunlu";
      if (user?.role !== "acente" && !form.agency_id) return "Acente seçin";
      if (!form.checkin || !form.checkout) return "Giriş ve çıkış tarihi zorunlu";
      if (form.checkout < form.checkin) return "Çıkış tarihi giriş tarihinden önce olamaz";
    }
    if (s === 1) {
      // Birleşik "Kişi & Oda" adımı: hem katılımcılar hem oda dağılımı.
      if (form.adults < 1) return "Yetişkin sayısı en az 1 olmalı";
      const roomsTotal = form.rooms.r1 + form.rooms.r2 + form.rooms.r3 + form.rooms.r4 + form.rooms.r5;
      if (roomsTotal === 0) return "En az bir oda tipi için sayı girilmiş olmalı";
    }
    // s === 2 (Fiyat) — birim fiyat opsiyonel; alışılmış davranış.
    return null;
  };

  const goNext = () => {
    const reason = validateStep(step);
    if (reason) return toast.error(reason);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  // Elevated warning triggered *only* on save-attempt with mismatch — the
  // inline warning inside step 4 handles the passive case. This state
  // controls the top-of-form banner + auto-scroll behaviour.
  const [saveMismatch, setSaveMismatch] = useState(false);
  const topWarnRef = useRef(null);
  const formPanelRef = useRef(null);

  // Iter 42 UX: adım değişince form panelini göz hizasına kaydır.
  // "start" yerine "center" kullanıyoruz — kullanıcı yeni step'e geçtiğinde
  // input alanları ekranın ortasında (rahat yazma pozisyonu) belirsin.
  useEffect(() => {
    if (!formPanelRef.current) return;
    // İlk render'da top scroll gereksiz — sadece step değişiklikleri için.
    const t = setTimeout(() => {
      try {
        formPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch { /* ignore */ }
    }, 60);
    return () => clearTimeout(t);
  }, [step]);

  // Guard against accidental submits caused by state transitions during
  // rapid clicks: after arriving on the final step, block the Kaydet
  // button for a short window so clicks that were intended for Devam
  // don't inadvertently fire submit at the same screen coordinates.
  const [submitReady, setSubmitReady] = useState(false);
  useEffect(() => {
    if (step === STEPS.length - 1) {
      setSubmitReady(false);
      const t = setTimeout(() => setSubmitReady(true), 500);
      return () => clearTimeout(t);
    } else {
      setSubmitReady(false);
    }
  }, [step]);

  const submit = async (e) => {
    e.preventDefault();
    // Block submits that fired the instant the user landed on the final
    // step — those are almost always the residual click event from the
    // previous Devam action, not an intentional Kaydet.
    if (!submitReady) return;
    // If submit fires while the user is not on the final step (e.g. an
    // implicit browser submit because they pressed Enter or a stray button
    // bubbled up), force them to the fiyat step instead of silently saving
    // with empty prices. Explicit save must come from the Kaydet button.
    if (step !== STEPS.length - 1) {
      setStep(STEPS.length - 1);
      toast.error("Kaydetmeden önce lütfen Fiyat & Not adımını doldurun");
      return;
    }
    // Validate every step before persisting — the user might have jumped
    // via the stepper header without going through validateStep each time.
    for (let s = 0; s < STEPS.length; s++) {
      const reason = validateStep(s);
      if (reason) {
        setStep(s);
        return toast.error(reason);
      }
    }
    if (showCapacityWarn) {
      // First surface a big banner at the top + auto-scroll so the user
      // sees the mismatch immediately, then open the blocking modal.
      setSaveMismatch(true);
      setTimeout(() => {
        topWarnRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        setCapacityModalOpen(true);
      }, 150);
      return;
    }
    setSaveMismatch(false);
    await doPersist();
  };

  const handleKeyDown = (e) => {
    // Enter in an <input> should advance to the next step, not submit the
    // form. Textareas keep their newline behaviour.
    if (e.key === "Enter" && e.target.tagName === "INPUT") {
      // ALWAYS preventDefault so Enter never triggers an implicit form
      // submit — that used to silently save the reservation as soon as
      // the user pressed Enter on the price field (step 5), skipping the
      // rest of the pricing inputs. User must click Kaydet explicitly.
      e.preventDefault();
      if (step < STEPS.length - 1) goNext();
    }
  };

  // Query-param modes from ReservationMethodChooser:
  //   ?mode=manual → hide AI photo card entirely (agency explicitly chose manual).
  //   ?mode=ai     → keep AI card (already used above to auto-open picker).
  const isManualMode = searchParams.get("mode") === "manual";
  const showAiCard = !isEdit && !isManualMode;

  return (
    <div ref={topWarnRef} className="max-w-3xl mx-auto space-y-6" data-testid="acente-reservation-form-page">
      <div className="text-center sm:text-left">
        <button onClick={() => nav(-1)} className="text-sm text-brand-navy/60 hover:text-brand-orange inline-flex items-center gap-1" data-testid="acente-form-back">
          <ArrowLeft size={14} /> Geri
        </button>
        <h1 className="mt-2 font-serif text-3xl text-brand-navy">
          {isEdit ? "Rezervasyonu Düzenle" : "Yeni Rezervasyon Oluştur"}
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Adım adım ilerleyin — üstteki kutucuklara tıklayarak istediğiniz adıma dönebilirsiniz.
        </p>
      </div>

      {/* Save-triggered mismatch banner — only appears AFTER an attempted
          save with a room/people mismatch. Passive typing does not trigger
          this; the inline warning inside step 4 handles that case. */}
      {saveMismatch && showCapacityWarn && (
        <div
          className="rounded-3xl border-2 border-rose-400 bg-gradient-to-br from-rose-100 via-rose-50 to-white p-6 sm:p-8 relative overflow-hidden shadow-lg"
          data-testid="rez-save-mismatch-warning"
        >
          <div className="absolute -right-6 -top-6 w-40 h-40 rounded-full bg-rose-300/25 blur-2xl animate-pulse pointer-events-none" />
          <div className="relative flex items-start gap-5">
            <div className="w-14 h-14 rounded-2xl bg-rose-600 text-white flex items-center justify-center flex-shrink-0 shadow-md">
              <AlertTriangle size={30} strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-rose-700">Kaydedilemedi — Sayılar Tutmuyor</div>
              <h3 className="font-serif text-xl sm:text-2xl md:text-3xl leading-tight mt-1 text-rose-900">
                Odada kalan kişi sayısı ile yemek yiyecek kişi sayısı eşleşmiyor
              </h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl bg-white/70 backdrop-blur-sm border-2 border-rose-200 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold">Oda Kapasitesi</div>
                  <div className="font-serif text-4xl text-brand-navy mt-1 leading-none">{roomCapacity}</div>
                </div>
                <div className="rounded-2xl bg-white/70 backdrop-blur-sm border-2 border-rose-200 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold">Toplam Kişi</div>
                  <div className="font-serif text-4xl text-brand-navy mt-1 leading-none">{totalPeople}</div>
                </div>
              </div>
              <div className="mt-3 text-sm text-rose-900">
                {capacityDiff > 0
                  ? <>Oda kapasitesi kişi sayısından <b>{capacityDiff} kişi</b> fazla.</>
                  : <>Kişi sayısı oda kapasitesinden <b>{Math.abs(capacityDiff)} kişi</b> fazla.</>}
                {" "}
                <button
                  type="button"
                  onClick={() => { setStep(1); setSaveMismatch(false); }}
                  className="ml-2 underline font-semibold hover:text-rose-700"
                  data-testid="rez-save-mismatch-fix-btn"
                >
                  Oda dağılımını düzelt →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Live calculation strip */}
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        <LiveTile icon={<Calendar size={16}/>} label="Gece" value={nights} accent="brand-navy" />
        <LiveTile icon={<BedDouble size={16}/>} label="Oda" value={totalRooms} accent="brand-orange" />
        <LiveTile icon={<Users size={16}/>} label="Kapasite" value={roomCapacity} accent="brand-navy" testid="rez-live-room-capacity" />
        <LiveTile icon={<Users size={16}/>} label="Kişi" value={totalPeople} accent="brand-gold" testid="rez-live-total-people" />
      </div>

      {/* AI photo shortcut — only for new reservations AND when the agency
          did NOT explicitly pick manual mode from the chooser. */}
      {showAiCard && (
        <div
          className="rounded-2xl border border-brand-orange/30 bg-gradient-to-br from-brand-orange/10 via-white to-brand-gold/10 p-4 sm:p-5 shadow-sm"
          data-testid="ai-photo-upload-card"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-orange to-brand-orange-dark text-white flex items-center justify-center shadow-md">
              <Sparkles size={20} />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold">Yapay Zeka Yardımcısı</div>
              <div className="font-serif text-lg text-brand-navy mt-0.5">Fotoğraftan bilgileri otomatik doldur</div>
              <p className="text-xs text-slate-600 mt-1">
                Elle yazılmış not, ekran görüntüsü ya da rezervasyon formunun fotoğrafını yükleyin — sistem otel adı, grup adı, tarihler, oda dağılımı ve kişi sayılarını otomatik doldurur.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={aiFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                hidden
                onChange={handleAiUpload}
                data-testid="ai-photo-input"
              />
              <Button
                type="button"
                onClick={() => aiFileRef.current?.click()}
                disabled={aiBusy}
                className="bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-70"
                data-testid="ai-photo-upload-btn"
              >
                {aiBusy ? <><Loader2 size={14} className="mr-2 animate-spin" /> AI analiz ediyor…</> : <><Camera size={14} className="mr-2" /> Fotoğraf Yükle</>}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Manual-mode users still need SOMEWHERE to receive AI-extracted
          files; expose a hidden input so the aiFileRef auto-click path can
          run even without the card in the DOM. */}
      {!showAiCard && !isEdit && (
        <input
          ref={aiFileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          onChange={handleAiUpload}
          data-testid="ai-photo-input"
        />
      )}

      {/* Capacity warning has moved inline into Step 4 (Oda Dağılımı) so it
          doesn't cause the layout to jump above the input as the agency
          types. A save-attempted mismatch surfaces at the very top via
          `rez-save-mismatch-warning` (see above). */}

      {/* Stepper */}
      <ol className="grid gap-2 sm:grid-cols-5" data-testid="rez-stepper">
        {STEPS.map((st, i) => {
          const done = i < step;
          const active = i === step;
          const Icon = st.icon;
          return (
            <li key={st.key}>
              <button
                type="button"
                onClick={() => setStep(i)}
                className={`w-full text-left rounded-2xl border p-3 flex items-center gap-3 transition-colors ${
                  active
                    ? "border-brand-orange bg-brand-orange/10 text-brand-navy shadow-sm"
                    : done
                      ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                      : "border-slate-200 bg-white text-slate-500 hover:text-brand-navy hover:border-brand-navy/30"
                }`}
                data-testid={`rez-step-${i}-${st.key}`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  active ? "bg-brand-orange text-white" : done ? "bg-emerald-200 text-emerald-800" : "bg-slate-100 text-slate-500"
                }`}>
                  {done ? <CheckCircle2 size={16} /> : <Icon size={16} />}
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest opacity-70">Adım {i + 1}</div>
                  <div className="text-sm font-semibold leading-tight truncate">{st.label}</div>
                </div>
              </button>
            </li>
          );
        })}
      </ol>

      <form
        ref={formPanelRef}
        onSubmit={submit}
        onKeyDown={handleKeyDown}
        className="bg-white rounded-2xl border border-brand-navy/10 p-6 sm:p-8 grid gap-6 shadow-sm"
        data-testid="acente-reservation-form"
      >
        {step === 0 && (
          <StepShell title="Otel, Grup ve Tarihler" hint="Rezervasyonu hangi otelde, hangi grup için ve hangi tarihler arasında oluşturuyorsunuz? Otel adına tıklayınca liste açılır.">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>Otel Adı *</Label>
                <HotelCombobox
                  value={form.hotel_name}
                  onChange={(v) => setField("hotel_name", v)}
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Listeden seçin veya yeni bir otel adı yazın. Yeni girdiğiniz oteller sonraki rezervasyonlarda listeye eklenir.
                </p>
              </div>
              <div>
                <Label>Grup / Müşteri Adı <span className="text-slate-400">(opsiyonel)</span></Label>
                <Input
                  value={form.group_name}
                  onChange={(e) => setField("group_name", e.target.value)}
                  placeholder="Örn. Ali Bey Grubu"
                  data-testid="rez-group-name"
                  autoFocus
                />
              </div>
              {user?.role !== "acente" && (
                <div>
                  <Label>Acente *</Label>
                  <select value={form.agency_id} onChange={(e) => setField("agency_id", e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="rez-agency">
                    <option value="">— Seçin —</option>
                    {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* Tarihler — otel/grup ile aynı sayfada, tek bir vertical flow. */}
            <div className="pt-3 border-t border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
                <Calendar size={12} className="text-brand-orange" /> Konaklama Tarihleri
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Giriş Tarihi *</Label>
                  <DatePicker value={form.checkin} onChange={(v) => setField("checkin", v)} required placeholder="Giriş tarihi seçin" data-testid="rez-checkin" />
                </div>
                <div>
                  <Label>Çıkış Tarihi *</Label>
                  <DatePicker value={form.checkout} onChange={(v) => setField("checkout", v)} required minDate={form.checkin || undefined} placeholder="Çıkış tarihi seçin" data-testid="rez-checkout" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-[11px] uppercase tracking-widest text-slate-500 mr-1 self-center">Hızlı süre:</span>
                {[3, 5, 7, 10, 14].map((n) => (
                  <Button
                    key={n}
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => bumpCheckout(n)}
                    data-testid={`rez-quick-${n}n`}
                    disabled={!form.checkin}
                    className="text-xs border-brand-orange/30 hover:bg-brand-orange/10 disabled:opacity-50"
                  >
                    +{n} gece
                  </Button>
                ))}
                {nights > 0 && (
                  <span className="ml-auto text-xs text-brand-navy self-center">
                    Toplam <b>{nights}</b> gece
                  </span>
                )}
              </div>
            </div>
          </StepShell>
        )}

        {step === 1 && (
          <StepShell title="Kişi, Yemek ve Oda" hint="Grup büyüklüğü, yemek planı ve oda dağılımı tek sayfada — otomatik oda önerisi hazır.">
            {/* Kişi sayıları */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label>Yetişkin Sayısı *</Label>
                <NumberInput min={1} value={form.adults} onChange={(v) => setForm((f) => ({ ...f, adults: Math.max(1, v || 0) }))} required data-testid="rez-adults" />
              </div>
              <div>
                <Label>Çocuk Sayısı</Label>
                <NumberInput min={0} value={form.children} onChange={(v) => setForm((f) => ({ ...f, children: Math.max(0, v || 0) }))} data-testid="rez-children" />
              </div>
              <div>
                <Label>Görevli Sayısı</Label>
                <NumberInput min={0} value={form.staff} onChange={(v) => setForm((f) => ({ ...f, staff: Math.max(0, v || 0) }))} data-testid="rez-staff" />
              </div>
            </div>

            {/* Yemek planı */}
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-center gap-2 text-xs text-slate-600">
              <Utensils size={14} className="text-brand-orange" />
              <span>Toplam <b>{totalPeople}</b> kişi için hangi öğünlerle başlayıp bitirmek istiyorsunuz?</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Başlangıç Öğünü *</Label>
                <select value={form.meal_start} onChange={(e) => setField("meal_start", e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="rez-meal-start">
                  {MEAL_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
                  {form.meal_start === "ogle" && <option value="ogle">Öğle (eski)</option>}
                </select>
              </div>
              <div>
                <Label>Bitiş Öğünü *</Label>
                <select value={form.meal_end} onChange={(e) => setField("meal_end", e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="rez-meal-end">
                  {MEAL_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
                  {form.meal_end === "ogle" && <option value="ogle">Öğle (eski)</option>}
                </select>
              </div>
            </div>

            {/* Oda dağılımı */}
            <div className="pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-1.5">
                  <BedDouble size={12} className="text-brand-orange" /> Oda Dağılımı
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={suggestRooms}
                  disabled={totalPeople <= 0}
                  className="text-xs border-brand-orange/30 hover:bg-brand-orange/10 disabled:opacity-50"
                  data-testid="rez-suggest-rooms"
                >
                  <Wand2 size={13} className="mr-1" /> Otomatik oda dağılımı öner
                </Button>
              </div>
              <div className="text-xs text-slate-600 mb-2">
                Toplam kişi: <b className="text-brand-navy">{totalPeople}</b> · Oda kapasitesi: <b className="text-brand-navy">{roomCapacity}</b>
              </div>
              <div className="grid gap-3 grid-cols-2 sm:grid-cols-5">
                {["r1", "r2", "r3", "r4", "r5"].map((k) => {
                  const labels = { r1: "1 Kişilik", r2: "2 Kişilik", r3: "3 Kişilik", r4: "4 Kişilik", r5: "5 Kişilik" };
                  return (
                    <div key={k}>
                      <Label>{labels[k]}</Label>
                      <NumberInput min={0} value={form.rooms[k]} onChange={(v) => setForm((f) => ({ ...f, rooms: { ...f.rooms, [k]: Math.max(0, v || 0) } }))} data-testid={`rez-room-${k}`} />
                    </div>
                  );
                })}
              </div>

              {/* Inline capacity warning */}
              {showCapacityWarn && (
                <div
                  className={`mt-3 rounded-2xl border-2 p-4 flex items-start gap-3 ${capacityDiff > 0 ? "border-amber-400 bg-amber-50" : "border-rose-400 bg-rose-50"}`}
                  data-testid="rez-capacity-warning-inline"
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${capacityDiff > 0 ? "bg-amber-500 text-white" : "bg-rose-600 text-white"}`}>
                    <AlertTriangle size={18} />
                  </div>
                  <div className="text-sm">
                    <div className={`font-semibold ${capacityDiff > 0 ? "text-amber-900" : "text-rose-900"}`}>
                      Oda kapasitesi ve kişi sayısı uyuşmuyor
                    </div>
                    <div className={`text-xs mt-1 ${capacityDiff > 0 ? "text-amber-800" : "text-rose-800"}`}>
                      Kapasite <b>{roomCapacity}</b> · Kişi <b>{totalPeople}</b>
                      {capacityDiff > 0 ? <> — <b>{capacityDiff}</b> kişilik fazla yer var.</> : <> — <b>{Math.abs(capacityDiff)}</b> kişi yetersiz.</>}
                    </div>
                  </div>
                </div>
              )}
              {totalRooms > 0 && totalPeople > 0 && capacityDiff === 0 && (
                <div
                  className="mt-3 rounded-2xl border border-emerald-300 bg-emerald-50 text-emerald-900 p-3 flex items-center gap-3 text-sm"
                  data-testid="rez-capacity-ok-inline"
                >
                  <div className="w-8 h-8 rounded-xl bg-emerald-200 text-emerald-800 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 size={15} />
                  </div>
                  <div>
                    <b>Oda ve kişi sayınız uyumlu.</b> {totalPeople} kişi için oda kapasiteniz tam {roomCapacity} — mükemmel.
                  </div>
                </div>
              )}
            </div>
          </StepShell>
        )}

        {step === 2 && (
          <StepShell title="Anlaşılan Fiyat ve Notlar" hint="Oda ve yemek birim fiyatlarını girin — sistem toplam anlaşılan tutarı otomatik hesaplar.">
            <div className="rounded-2xl bg-brand-orange/5 border border-brand-orange/20 p-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand-orange-dark font-semibold">
                <Wallet size={14} /> Anlaşılan Fiyatı Girin
              </div>
              <div className="mt-2 rounded-xl bg-white border border-brand-orange/30 p-3 text-sm text-brand-navy">
                <div className="flex items-start gap-2">
                  <div className="w-6 h-6 rounded-full bg-brand-orange/15 text-brand-orange-dark flex items-center justify-center flex-shrink-0 mt-0.5">
                    <AlertTriangle size={12} />
                  </div>
                  <div>
                    <p className="font-medium">Birim fiyatları girin — toplam otomatik hesaplanır.</p>
                    <ul className="text-xs text-slate-600 mt-1 space-y-0.5 list-disc list-inside">
                      <li><b>Oda Fiyatı:</b> 1 oda × 1 gece için anlaştığınız fiyat</li>
                      <li><b>Yemek Fiyatı:</b> 1 kişi × 1 tam gün (2 öğün) için anlaştığınız fiyat</li>
                      <li>Yemek toplamı yalnızca <b>yetişkin sayısı</b> üzerinden hesaplanır (çocuk ve görevli hariç)</li>
                    </ul>
                    <p className="text-xs text-slate-600 mt-1"><b>Not:</b> Girdiğiniz tutar operasyon ekibimiz tarafından doğrulanacaktır. Onay sonrası bu tutar rezervasyonunuza sabitlenir.</p>
                  </div>
                </div>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Oda Fiyatı <span className="text-[11px] text-slate-500">— 1 oda / 1 gece · SAR ﷼</span></Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.room_unit_price}
                    onChange={(e) => setField("room_unit_price", Math.max(0, parseFloat(e.target.value || 0)))}
                    data-testid="rez-room-unit-price"
                    {...numberFieldProps}
                  />
                </div>
                <div>
                  <Label>Yemek Fiyatı <span className="text-[11px] text-slate-500">— 1 kişi / 1 gün (2 öğün) · SAR ﷼</span></Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.meal_unit_price}
                    onChange={(e) => setField("meal_unit_price", Math.max(0, parseFloat(e.target.value || 0)))}
                    data-testid="rez-meal-unit-price"
                    {...numberFieldProps}
                  />
                </div>
              </div>

              {/* Live breakdown so the agency understands what they'll be
                  billed for. Numbers refresh instantly as they type. */}
              {(() => {
                const nights = _computedNights(form.checkin, form.checkout);
                const t = computeReservationTotal({
                  rooms: form.rooms,
                  nights,
                  adults: form.adults,
                  children: form.children,
                  staff: form.staff,
                  meal_start: form.meal_start,
                  meal_end: form.meal_end,
                  room_unit_price: form.room_unit_price,
                  meal_unit_price: form.meal_unit_price,
                });
                if (t.roomTotal <= 0 && t.mealTotal <= 0) return null;
                return (
                  <div className="mt-3 space-y-2" data-testid="rez-price-breakdown">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="rounded-xl bg-white border border-brand-orange/30 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Oda Toplamı</div>
                        <div className="text-xs text-slate-600 mt-0.5">{t.totalRooms} oda × {nights} gece × {Number(form.room_unit_price || 0).toLocaleString("tr-TR")} ﷼</div>
                        <div className="font-serif text-xl text-brand-navy mt-1" data-testid="rez-room-total">
                          {t.roomTotal.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼
                        </div>
                      </div>
                      <div className="rounded-xl bg-white border border-brand-orange/30 p-3">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Yemek Toplamı</div>
                        <div className="text-xs text-slate-600 mt-0.5">{t.totalMeals} öğün × {t.mealPersons} yetişkin × {t.perMealPrice.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼ (öğün başı)</div>
                        <div className="font-serif text-xl text-brand-navy mt-1" data-testid="rez-meal-total">
                          {t.mealTotal.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-brand-orange to-brand-orange-dark text-white px-4 py-3 shadow-md">
                      <div className="text-xs uppercase tracking-widest font-semibold">Toplam Anlaşılan Fiyat</div>
                      <div className="font-serif text-2xl" data-testid="rez-agreed-total">
                        {t.grandTotal.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}{" "}
                        <span className="text-sm opacity-90">SAR ﷼</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div>
              <Label className="inline-flex items-center gap-1.5">
                <StickyNote size={13} className="text-brand-orange" /> Özel Notlar
              </Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                rows={4}
                placeholder="Giriş saatinizi, pasta yapımı gibi özel istekleri buraya yazabilirsiniz."
                data-testid="rez-notes"
              />
            </div>
          </StepShell>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => nav("/acente/rezervasyonlar")} data-testid="rez-cancel">
              Vazgeç
            </Button>
            {step > 0 && (
              <Button type="button" variant="outline" onClick={goBack} data-testid="rez-step-back">
                <ArrowLeft size={14} className="mr-1" /> Geri
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                onClick={goNext}
                className="bg-brand-orange text-white hover:bg-brand-orange-dark"
                data-testid="rez-step-next"
              >
                Devam <ArrowRight size={14} className="ml-1" />
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={busy || !submitReady}
                className="bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-60"
                data-testid="rez-submit"
              >
                <Save size={16} className="mr-1" /> {busy ? "Kaydediliyor..." : (isEdit ? "Güncelle" : "Kaydet")}
              </Button>
            )}
          </div>
        </div>
      </form>

      <CapacityMismatchModal
        open={capacityModalOpen}
        onCancel={() => setCapacityModalOpen(false)}
        onConfirm={doPersist}
        roomCapacity={roomCapacity}
        totalPeople={totalPeople}
        adults={form.adults}
        children={form.children}
        staff={form.staff}
        rooms={form.rooms}
      />
    </div>
  );
}

function StepShell({ title, hint, children }) {
  return (
    <section className="grid gap-4">
      <div>
        <h2 className="font-serif text-xl text-brand-navy">{title}</h2>
        {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function LiveTile({ icon, label, value, accent, testid }) {
  const bgMap = {
    "brand-navy": "from-brand-navy/10 to-brand-navy/5 text-brand-navy",
    "brand-orange": "from-brand-orange/10 to-brand-orange/5 text-brand-orange-dark",
    "brand-gold": "from-brand-gold/20 to-brand-gold/5 text-brand-navy",
  };
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${bgMap[accent]} border border-white p-4 flex items-center gap-3`} data-testid={testid}>
      <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
        <div className="font-serif text-2xl leading-none mt-0.5">{value}</div>
      </div>
    </div>
  );
}
